import { useSessionStorageValue } from "@react-hookz/web";
import { useCallback, useEffect, useRef, useState } from "react";

import t from "../../../i18n";
import { useModal } from "../../../providers/ModalProvider";
import { useApi } from "../../../use/api";
import FormInput from "../../forms/FormInput";
import Input from "../../forms/Input";
import Button from "../../ui/Button";

export default function PasswordModal() {
  const { value: doNotOffer } = useSessionStorageValue("doNotOfferPassword", {
    defaultValue: false,
  });
  const { addModal } = useModal();

  useEffect(() => {
    if (doNotOffer) return;

    addModal({
      title: t("Set a password"),
      cancelText: t("Not now"),
      confirmText: t("Set password"),
      content: <PasswordModalContent />,
    });
  }, [doNotOffer, addModal]);

  return null;
}

const PasswordModalContent = () => {
  const { set: setDoNotOffer } = useSessionStorageValue("doNotOfferPassword", {
    defaultValue: false,
  });
  const { addModal, removeModal, bindModalClick } = useModal();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | undefined>();
  const { call, isLoading } = useApi("auth.password.submit");

  const onSubmit = useCallback(
    async (evt?: React.FormEvent) => {
      evt?.preventDefault();
      const response = await call({
        password: inputRef.current?.value ?? "",
      });

      if (response.success) {
        if (evt) removeModal();

        addModal({
          type: "success",
          title: response.preEnabled ? t("Password set") : t("Please check your email"),
          content: response.preEnabled
            ? t("Your password has been set. You can now log in with your email and password.")
            : t(
                "We have sent you an email to confirm your password. Follow the link in the email to set your password.",
              ),
          replace: true,
        });
        return true;
      } else if (response.error === "validation_error") {
        switch (response.userErrors.password) {
          case "password_not_allowed_by_team":
            setError(t("Password not allowed by team"));
            return false;
          case "password_minimum_8_characters":
            setError(t("Password must be at least 8 characters long"));
            return false;
          case "password_must_not_be_a_common_password":
            setError(t("Password must not be a common password"));
            return false;
          case "password_must_contain_letters_and_non_letters":
            setError(t("Password must contain letters and non letters"));
            return false;
        }
      }
    },
    [removeModal, addModal, call],
  );

  useEffect(() => {
    setTimeout(() => {
      // Wait for modal to open
      inputRef.current?.focus();
    }, 100);
  }, [inputRef]);

  useEffect(() => {
    bindModalClick((confirm: boolean) => {
      if (confirm) return onSubmit();
      else setDoNotOffer(true);
    });
  }, [bindModalClick, onSubmit, setDoNotOffer]);

  return (
    <form onSubmit={onSubmit}>
      <p>{t("Setting a password allows you to log into Upscope more quickly and securely.")}</p>
      <FormInput
        label={t("Password")}
        explanation={t("Make sure it is long and secure.")}
        className="!mb-0 !mt-5 text-left"
        error={error}
      >
        <Input type="password" ref={inputRef} required autoComplete="new-password" />
      </FormInput>
      <Button type="submit" className="hidden" loading={isLoading}>
        Submit
      </Button>{" "}
      {/* for enter key to submit form */}
    </form>
  );
};
